// apps/share-web/src/chart/sheet-settings.ts
//
// Лист настроек чтения за одной кнопкой ⚙ (words page v2, спека 2026-09-14).
// Сергей: «чтобы эти настройки одной кнопочкой вызывались… чтобы это всё
// постоянно не висело и больше места было на телефоне».
//
// Ряды сверху вниз: Key (родной <select>, все 15 сдвигов — mountKeySelect),
// Columns (1 · 2 · 3 всегда), Text size (степпер), Theme (Dark · Light ·
// Auto), Reading (Fit whole song · Follow by tempo), Show (Chords · Lyrics).
// Каждое изменение уходит владельцу сразу и применяется к листу под окном;
// лист закрывается тапом по скриму — размер подбирают несколькими нажатиями,
// закрывать после первого было бы издевательством.
//
// Лист ничего не хранит и не знает про localStorage: он рисует значения,
// которые ему дали, и сообщает о выборе. Память — у владельца (reader-prefs).

import { mountKeySelect, mountFontStepper } from './chart-controls.js';
import { textButton, type BwButtonEl } from './chart-buttons.js';
import { READER_COLUMNS_MAX, type ReaderTheme } from './reader-prefs.js';
import { t } from '../ui/i18n.js';

export interface SheetSettingsDeps {
  songKey: string;
  shift: number;
  columns: number;
  fontPct: number;
  theme: ReaderTheme;
  fit: boolean;
  follow: boolean;
  /** У песни есть bpm и секции — ведение по темпу возможно. */
  followAvailable: boolean;
  showChords: boolean;
  showLyrics: boolean;
  onShift(n: number): void;
  onColumns(n: number): void;
  onFontPct(n: number): void;
  onTheme(theme: ReaderTheme): void;
  onFit(on: boolean): void;
  onFollow(on: boolean): void;
  onShowChords(on: boolean): void;
  onShowLyrics(on: boolean): void;
  /**
   * Скопировать слова песни для слайдов проектора: `true` — легло в буфер,
   * `false` — буфер отказал, `'empty'` — слов нет (песня из одних аккордов).
   * Нет поля — нет ряда (B-261002-web-words-link-text-not-copyable).
   */
  onCopyLyrics?: () => Promise<boolean | 'empty'>;
}

export interface SheetSettingsHandle {
  open(): void;
  close(): void;
  isOpen(): boolean;
  /** Песня сменилась — доступность ведения по темпу тоже. */
  setFollowAvailable(on: boolean): void;
  /** Результат подбора «вся песня на экране»: false — показать «не помещается». */
  setFitFits(fits: boolean): void;
  destroy(): void;
}

const el = (tag: string, cls: string, txt?: string): HTMLElement => {
  const e = document.createElement(tag);
  e.className = cls;
  if (txt != null) e.textContent = txt;
  return e;
};

/**
 * Надпись ДС-кнопки. `bw-button` живёт в светлом DOM и после первой отрисовки
 * переносит текст автора в свой `.bw-button-label`: `textContent` хоста стёр бы
 * всю его разметку. До отрисовки (и в тестах без апгрейда) надпись — сам хост.
 */
function setLabel(b: HTMLElement, text: string): void {
  const label = b.querySelector('.bw-button-label');
  if (label) label.textContent = text;
  else b.textContent = text;
}

function row(label: string, control: HTMLElement, cls: string): HTMLElement {
  const r = el('div', `cw-set-row ${cls}`);
  r.appendChild(el('div', 'cw-set-lbl', label));
  control.classList.add('cw-set-ctl');
  r.appendChild(control);
  return r;
}

/** Сегменты: ровно один выбран. Кнопки ДС, состояние — `selected` компонента. */
function segments<T extends string | number>(
  items: Array<{ value: T; text: string }>,
  current: T,
  onPick: (v: T) => void,
  cls: string,
): { el: HTMLElement; set(v: T): void } {
  const box = el('div', `cw-seg ${cls}`);
  box.setAttribute('role', 'group');
  const btns = new Map<T, BwButtonEl>();
  let cur = current;
  const paint = (): void => { for (const [v, b] of btns) b.selected = v === cur; };
  for (const it of items) {
    const b = textButton({ text: it.text, size: 'md', cls: 'cw-seg-btn', onClick: () => { if (it.value === cur) return; cur = it.value; paint(); onPick(it.value); } });
    b.dataset.value = String(it.value);
    btns.set(it.value, b);
    box.appendChild(b);
  }
  paint();
  return { el: box, set(v) { cur = v; paint(); } };
}

/** Переключатель: одна ДС-кнопка с `selected`. */
function toggle(text: string, on: boolean, onFlip: (on: boolean) => void, cls: string): { el: BwButtonEl; set(on: boolean): void } {
  let cur = on;
  const b = textButton({ text, size: 'md', cls: `cw-toggle ${cls}`, onClick: () => { if (b.disabled) return; cur = !cur; b.selected = cur; onFlip(cur); } });
  b.selected = cur;
  return { el: b, set(v) { cur = v; b.selected = v; } };
}

export function mountSheetSettings(host: HTMLElement, deps: SheetSettingsDeps): SheetSettingsHandle {
  const scrim = el('div', 'cw-set-scrim');
  scrim.hidden = true;
  const sheet = el('div', 'cw-settings');
  sheet.hidden = true;
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-label', t('chart.settings'));

  // Key — родной select, все 15 сдвигов, подписи от тональности песни.
  const keyBox = el('div', 'cw-set-key');
  mountKeySelect(keyBox, { songKey: deps.songKey, shift: deps.shift, onChange: (n) => deps.onShift(n) });
  sheet.appendChild(row(t('chart.keyLabel'), keyBox, 'cw-set-row-key'));

  // Columns — 1 · 2 · 3 всегда (решение Сергея 14.09; потолок телефона снят).
  const cols = segments(
    Array.from({ length: READER_COLUMNS_MAX }, (_, i) => ({ value: i + 1, text: String(i + 1) })),
    Math.max(1, Math.min(READER_COLUMNS_MAX, Math.round(deps.columns) || 1)),
    (n) => deps.onColumns(n),
    'cw-seg-cols',
  );
  sheet.appendChild(row(t('chart.columnsLabel'), cols.el, 'cw-set-row-cols'));

  // Text size — степпер без потолка шкалы.
  const fontBox = el('div', 'cw-set-font');
  mountFontStepper(fontBox, { pct: deps.fontPct, onChange: (n) => deps.onFontPct(n) });
  sheet.appendChild(row(t('chart.fontLabel'), fontBox, 'cw-set-row-font'));

  // Theme.
  const theme = segments<ReaderTheme>(
    [{ value: 'dark', text: t('chart.themeDark') }, { value: 'light', text: t('chart.themeLight') }, { value: 'auto', text: t('chart.themeAuto') }],
    deps.theme,
    (v) => deps.onTheme(v),
    'cw-seg-theme',
  );
  sheet.appendChild(row(t('chart.themeLabel'), theme.el, 'cw-set-row-theme'));

  // Reading — два независимых переключателя.
  const reading = el('div', 'cw-toggles cw-toggles-reading');
  const fit = toggle(t('chart.fitWholeSong'), deps.fit, (on) => deps.onFit(on), 'cw-toggle-fit');
  const follow = toggle(t('chart.followTempo'), deps.follow, (on) => deps.onFollow(on), 'cw-toggle-follow');
  reading.append(fit.el, follow.el);
  sheet.appendChild(row(t('chart.readingLabel'), reading, 'cw-set-row-reading'));
  const followHint = el('div', 'cw-set-hint cw-set-hint-follow', t('chart.followNeedsSections'));
  const fitHint = el('div', 'cw-set-hint cw-set-hint-fit', t('chart.fitDoesNotFit'));
  fitHint.hidden = true;
  sheet.append(followHint, fitHint);

  // Show — аккорды и слова, любой или оба можно выключить (дополнение Сергея).
  const show = el('div', 'cw-toggles cw-toggles-show');
  const chords = toggle(t('chart.showChords'), deps.showChords, (on) => deps.onShowChords(on), 'cw-toggle-chords');
  const lyrics = toggle(t('chart.showLyrics'), deps.showLyrics, (on) => deps.onShowLyrics(on), 'cw-toggle-lyrics');
  show.append(chords.el, lyrics.el);
  sheet.appendChild(row(t('chart.showLabel'), show, 'cw-set-row-show'));

  // For slides — слова песни в буфер: только слова, блок на слайд (решение
  // Сергея 06.10). Надпись на кнопке честно говорит, легло ли в буфер, и сама
  // возвращается: человек копирует песню за песней.
  let copyTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  if (deps.onCopyLyrics) {
    const onCopy = deps.onCopyLyrics;
    const idle = t('chart.copyLyrics');
    const copyBox = el('div', 'cw-set-copy');
    // Смена надписи — единственный ответ на нажатие: читалке экрана её объявить.
    copyBox.setAttribute('aria-live', 'polite');
    const copy = textButton({
      text: idle, size: 'md', cls: 'cw-copy-lyrics',
      onClick: async () => {
        const res = await onCopy().catch(() => false as const);
        if (destroyed) return;
        setLabel(copy, res === 'empty' ? t('chart.copyLyricsNone')
          : res ? t('chart.copyLyricsDone') : t('chart.copyLyricsFailed'));
        if (copyTimer) clearTimeout(copyTimer);
        copyTimer = setTimeout(() => { setLabel(copy, idle); copyTimer = null; }, 2000);
      },
    });
    copyBox.appendChild(copy);
    sheet.appendChild(row(t('chart.copyLabel'), copyBox, 'cw-set-row-copy'));
    sheet.appendChild(el('div', 'cw-set-hint cw-set-hint-copy', t('chart.copyLyricsHint')));
  }

  const setFollowAvailable = (on: boolean): void => {
    follow.el.disabled = !on;
    followHint.hidden = on;
  };
  setFollowAvailable(deps.followAvailable);

  host.append(scrim, sheet);

  let open = false;
  const setOpen = (v: boolean): void => {
    open = v;
    scrim.hidden = !v;
    sheet.hidden = !v;
  };
  scrim.addEventListener('click', () => setOpen(false));

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    isOpen: () => open,
    setFollowAvailable,
    setFitFits(fits) { fitHint.hidden = fits; },
    destroy() { destroyed = true; if (copyTimer) clearTimeout(copyTimer); scrim.remove(); sheet.remove(); },
  };
}
